/**
 * Async Handler Manager
 *
 * Manages asynchronous handler promises and provides a clean API
 * for tracking loading state and waiting for all handlers to complete.
 */

import { FieldPath } from './types';

interface AsyncUpdate {
  path: FieldPath;
  value: any;
}

/**
 * Manages asynchronous handler promises
 */
export class AsyncHandlerManager {
  private promises = new Map<FieldPath, Promise<AsyncUpdate>>();

  /**
   * Adds an async handler promise for a field
   *
   * @param path - The field path
   * @param promise - The promise that resolves to the field value
   */
  add(path: FieldPath, promise: Promise<any>): void {
    // Wrap the promise to include the path in the result
    const wrappedPromise = promise.then(value => {
      return {
        path,
        value,
      };
    });

    this.promises.set(path, wrappedPromise);
  }

  /**
   * Checks if there are any pending async handlers
   *
   * @returns True if loading, false otherwise
   */
  isLoading(): boolean {
    return this.promises.size > 0;
  }

  /**
   * Gets the number of pending async handlers
   *
   * @returns Count of pending handlers
   */
  getPendingCount(): number {
    return this.promises.size;
  }

  /**
   * Waits for all pending async handlers to complete
   * and returns their results
   *
   * @returns Promise that resolves to array of updates
   */
  async waitAll(): Promise<AsyncUpdate[]> {
    if (this.promises.size === 0) {
      return [];
    }

    const promiseArray = Array.from(this.promises.values());
    this.promises.clear();

    try {
      const results = await Promise.all(promiseArray);
      return results;
    } catch (error) {
      // Clear promises even on error
      this.promises.clear();
      throw error;
    }
  }

  /**
   * Gets all pending promises (without clearing them)
   * Useful for integration with existing code
   *
   * @returns Array of promises
   */
  getPendingPromises(): Array<Promise<AsyncUpdate>> {
    return Array.from(this.promises.values());
  }

  /**
   * Clears all pending promises
   */
  clear(): void {
    this.promises.clear();
  }

  /**
   * Gets all pending field paths
   *
   * @returns Array of field paths with pending handlers
   */
  getPendingPaths(): FieldPath[] {
    return Array.from(this.promises.keys());
  }
}
